/**
 * Machine-readable error codes carried in an error response body alongside the HTTP status.
 *
 * A code is added only where the status alone leaves the client unable to act: a player user whose
 * account is not linked to a roster player and a player user asking for an endpoint they may not
 * have both answer `403`, and only one of the two has a screen to show. This is deliberately not a
 * second, parallel status vocabulary — most responses need no code at all.
 */
export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export const ApiErrorCode = {
  /**
   * The roster player an invitation names already has an account linked to it.
   *
   * A `409` on invitation creation has three causes — this, {@link
   * ApiErrorCode.PLAYER_ALREADY_INVITED}, and the invited address already having an account — and
   * each asks the coach for a different fix, so the two that are about the player carry a code.
   */
  PLAYER_ALREADY_LINKED: 'player_already_linked',
  /** The roster player an invitation names already has an invitation outstanding. */
  PLAYER_ALREADY_INVITED: 'player_already_invited',
  /**
   * The caller is a player user whose mapping points at a roster player that no longer exists —
   * almost always because a coach deleted it from the roster after the account was linked.
   *
   * Distinct from {@link ApiErrorCode.PLAYER_NOT_LINKED} because the two are different states of
   * the account even though both leave the player unable to continue: one was never linked, the
   * other was linked to something that has since been removed. Keeping them apart is what makes a
   * dangling mapping visible in the logs instead of hiding inside the never-linked bucket.
   */
  PLAYER_DELETED: 'player_deleted',
  /** The caller is a player user whose account is not linked to any roster player. */
  PLAYER_NOT_LINKED: 'player_not_linked',
} as const;

/**
 * Whether an error response body carries the given {@link ApiErrorCode}.
 *
 * Takes `unknown` because callers hold whatever the HTTP client parsed out of the response, which
 * is never typed and may be a string, `null`, or a body written by a proxy that the server never
 * saw.
 */
export function hasApiErrorCode(body: unknown, code: ApiErrorCode): boolean {
  return getApiErrorCode(body) === code;
}

const API_ERROR_CODES = new Set<string>(Object.values(ApiErrorCode));

/**
 * The {@link ApiErrorCode} an error response body carries, or `null` if it carries none we know.
 *
 * The counterpart to {@link hasApiErrorCode}, for the callers that have to *choose* rather than
 * confirm: a screen picking which explanation to show reads the code once and switches on it,
 * where asking `hasApiErrorCode` per candidate grows a new branch for every code and silently
 * falls through to the generic answer when a new one appears.
 *
 * Unrecognised codes come back as `null` rather than as themselves. A body can be written by a
 * proxy the server never reached, and a value that only looks like a code has no message behind
 * it — so callers get "nothing specific to say" instead of a string they cannot translate.
 */
export function getApiErrorCode(body: unknown): ApiErrorCode | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const { code } = body as { code?: unknown };

  return typeof code === 'string' && API_ERROR_CODES.has(code) ? (code as ApiErrorCode) : null;
}
